
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL, TENANT_ID } from "../../config";
import { getMessage } from "../../data/locales";
import { clearSessionKeys } from "../../utils/sessionKeys";
import "./CheckEmail.css";

const CheckEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state && typeof location.state === "object" ? location.state : {};
  const emailFromState = typeof locationState.email === "string" ? locationState.email : "";
  const pedidoFromState = typeof locationState.pedidoId === "string" ? locationState.pedidoId : "";
  const pedidoId =
    pedidoFromState || new URLSearchParams(location.search).get("orderId") || "";
  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (pedidoId) {
      localStorage.setItem("pendingOrderId", pedidoId);
    }
  }, [pedidoId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    const trimmedEmail = email.trim();
    const digits = code.replace(/\D/g, "").slice(0, 6);
    if (!trimmedEmail) {
      setError(getMessage("checkEmail.missingEmail"));
      return;
    }
    if (digits.length !== 6 || digits === "000000") {
      setError(getMessage("checkEmail.invalidCode"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/verify-email`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": TENANT_ID,
        },
        body: JSON.stringify({ email: trimmedEmail, code: digits }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || getMessage("checkEmail.invalidCode"));
        return;
      }
      clearSessionKeys();
      const resolvedOrderId =
        (typeof data.pendingOrderId === "string" && data.pendingOrderId) || pedidoId;
      if (resolvedOrderId) {
        localStorage.setItem("pendingOrderId", resolvedOrderId);
      }
      if (data.setupRequired && data.setupToken) {
        const setupParams = new URLSearchParams({ token: data.setupToken });
        if (resolvedOrderId) {
          setupParams.set("orderId", resolvedOrderId);
        }
        navigate(`/setup-account?${setupParams.toString()}`, { replace: true });
        return;
      }
      if (resolvedOrderId) {
        navigate(`/checkout?orderId=${resolvedOrderId}`, { replace: true });
        return;
      }
      navigate("/login", { replace: true });
    } catch {
      setError(getMessage("checkEmail.connectionError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="check-email-container">
      <div className="check-email-card">
        <div className="icon-container">
          📩
        </div>
        <h1>{getMessage("checkEmail.title")}</h1>
        <p>{getMessage("checkEmail.intro")}</p>
        <form className="check-email-form" onSubmit={handleSubmit}>
          <label htmlFor="check-email-address">{getMessage("checkEmail.emailLabel")}</label>
          <input
            id="check-email-address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <label htmlFor="check-email-code">{getMessage("checkEmail.codeLabel")}</label>
          <input
            id="check-email-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder={getMessage("checkEmail.codePlaceholder")}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          {error ? <p className="check-email-error">{error}</p> : null}
          <button type="submit" className="check-email-submit" disabled={submitting}>
            {submitting ? getMessage("checkEmail.submitting") : getMessage("checkEmail.submit")}
          </button>
        </form>
        <p className="note">{getMessage("checkEmail.spamNote")}</p>
        <Link to="/login" className="back-link">{getMessage("checkEmail.backToLogin")}</Link>
      </div>
    </div>
  );
};

export default CheckEmail;
